import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TeamMemberCard = ({ member, variant = 'default' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleLinkedInClick = () => {
    window.open(member.linkedIn, '_blank', 'noopener,noreferrer');
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${member.email}`;
  };

  if (variant === 'leadership') {
    return (
      <div className="bg-white rounded-xl shadow-soft border border-border p-6 hover:shadow-elevation transition-smooth group">
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-4">
            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-primary/10 group-hover:border-primary/20 transition-smooth">
              <Image
                src={member.image}
                alt={`${member.name} - ${member.title}`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Award" size={16} color="white" />
            </div>
          </div>
          
          <h3 className="text-xl font-bold text-foreground mb-1">{member.name}</h3>
          <p className="text-primary font-semibold mb-2">{member.title}</p>
          <p className="text-muted-foreground text-sm mb-4 line-clamp-3">{member.bio}</p>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {member.expertise.slice(0, 3).map((skill, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
              >
                {skill}
              </span>
            ))}
            {member.expertise.length > 3 && (
              <span className="px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full">
                +{member.expertise.length - 3} more
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Linkedin"
              iconPosition="left"
              onClick={handleLinkedInClick}
            >
              Connect
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Mail"
              onClick={handleEmailClick}
            >
              <span className="sr-only">Email {member.name}</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-soft border border-border overflow-hidden hover:shadow-elevation transition-smooth group">
      <div className="p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-primary/10 group-hover:border-primary/20 transition-smooth">
              <Image
                src={member.image}
                alt={`${member.name} - ${member.title}`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-lg font-semibold text-foreground mb-1">{member.name}</h4>
                <p className="text-primary font-medium text-sm mb-2">{member.title}</p>
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Linkedin"
                  onClick={handleLinkedInClick}
                >
                  <span className="sr-only">LinkedIn profile</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Mail"
                  onClick={handleEmailClick}
                >
                  <span className="sr-only">Email contact</span>
                </Button>
              </div>
            </div>
            
            <p className="text-muted-foreground text-sm mb-3 line-clamp-2">
              {isExpanded ? member.bio : `${member.bio.substring(0, 120)}...`}
            </p>
            
            <div className="flex flex-wrap gap-1 mb-3">
              {member.expertise.slice(0, 4).map((skill, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
                >
                  {skill}
                </span>
              ))}
              {member.expertise.length > 4 && (
                <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                  +{member.expertise.length - 4}
                </span>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Icon name="Calendar" size={12} />
                  <span>{member.experience}+ years</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="Award" size={12} />
                  <span>{member.certifications} certs</span>
                </span>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                iconPosition="right"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? "Less" : "More"}
              </Button>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="space-y-3">
              <div>
                <h5 className="text-sm font-semibold text-foreground mb-2">Notable Projects</h5>
                <ul className="space-y-1">
                  {member.projects.map((project, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                      <Icon name="ChevronRight" size={12} className="mt-1 flex-shrink-0" />
                      <span>{project}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {member.testimonial && (
                <div>
                  <h5 className="text-sm font-semibold text-foreground mb-2">Client Testimonial</h5>
                  <blockquote className="text-sm text-muted-foreground italic border-l-2 border-primary/20 pl-3">
                    "{member.testimonial.quote}"
                    <footer className="text-xs mt-1 not-italic">
                      — {member.testimonial.client}, {member.testimonial.company}
                    </footer>
                  </blockquote>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMemberCard;